import { Stack, Typography } from "@mui/material";
import { InputWithSlider } from "Components/Merchant/FeeCalculator/InputWithSlider";
import type { CalculatorFormValues } from "Components/Merchant/FeeCalculator/schema";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import { Resources, useResource } from "Translations/Resources";
import type { Control } from "react-hook-form";
import { useMemo } from "react";

const COMPONENT_RESOURCES =
  Resources.Pages.Content.MerchantFeeCalculator.FeeSettings;

type FieldConfig = {
  label: string;
  name: keyof CalculatorFormValues;
  inputEndAdornment: string;
  description?: string;
  min?: number;
  max?: number;
  step?: number;
};

type FeeSettingsConfig = {
  title: string;
  topField: FieldConfig;
  bottomField?: FieldConfig;
};

type FeeType = "current" | "proposed" | "moone";

type Props = {
  control: Control<CalculatorFormValues>;
  type: FeeType;
};

export const FeeSettingWidget = ({ control, type }: Props) => {
  const { t } = useResource();

  const feeTypeToConfigMap = useMemo(
    (): Record<FeeType, FeeSettingsConfig> => ({
      current: {
        title: t(COMPONENT_RESOURCES.CurrentFees),
        topField: {
          label: t(COMPONENT_RESOURCES.ProviderFee),
          description: t(COMPONENT_RESOURCES.Acquiring),
          name: "currentProviderFee",
          inputEndAdornment: "%",
          min: 0,
          max: 5,
          step: 0.01,
        },
        bottomField: {
          label: t(
            Resources.Pages.Content.MerchantFeeCalculator.FeeSettings
              .FixedTransactionCost,
          ),
          name: "currentTransactionCost",
          inputEndAdornment: "Kč",
          min: 0,
          max: 5,
          step: 0.01,
        },
      },
      proposed: {
        title: t(COMPONENT_RESOURCES.ProposedFee),
        topField: {
          label: t(COMPONENT_RESOURCES.ProviderFee),
          description: t(COMPONENT_RESOURCES.Acquiring),
          name: "suggestedProviderFee",
          inputEndAdornment: "%",
          min: 0,
          max: 5,
          step: 0.01,
        },
        bottomField: {
          label: t(
            Resources.Pages.Content.MerchantFeeCalculator.FeeSettings
              .FixedTransactionCost,
          ),
          name: "suggestedTransactionCost",
          inputEndAdornment: "Kč",
          min: 0,
          max: 5,
          step: 0.01,
        },
      },
      moone: {
        title: t(
          Resources.Pages.Content.MerchantFeeCalculator.FeeSettings
            .DirectPaymentWithMoone,
        ),
        topField: {
          label: t(COMPONENT_RESOURCES.MooneFee),
          name: "mooneDirectPayFee",
          inputEndAdornment: "%",
          min: 0,
          max: 5,
          step: 0.01,
        },
      },
    }),
    [t],
  );

  const { title, topField, bottomField } = feeTypeToConfigMap[type];

  return (
    <PrimaryPaper sx={{ height: "100%" }}>
      <Typography variant="h5" textAlign="center">
        {title}
      </Typography>
      <Stack gap={1} mt={6}>
        <InputWithSlider {...topField} control={control} />
        {bottomField && <InputWithSlider {...bottomField} control={control} />}
      </Stack>
    </PrimaryPaper>
  );
};
